import { graphql, useStaticQuery } from "gatsby";
import { ContentTileProps } from "../components/elements/ContentTile/ContentTile";
import {
  KontentLinkedContentTile,
  KontentLinkedMediaImage,
  StringValue,
  Image,
  CollectionPage,
} from "../types";
import { formatContentTiles } from "./contentTiles";
import { formatImages } from "./images";

export interface UseScientists {
  scientists: Scientists[];
  pages: CollectionPage[];
}

export interface Scientists {
  page?: {
    url?: string;
    title?: string;
    displayPublishDate?: string;
  };
  id?: string;
  systemId?: string;
  firstName?: string;
  lastName?: string;
  image?: {
    largeImage: Image;
    smallImage: Image;
  };
  pageDescription?: string;
  date?: string;
  contentTile?: ContentTileProps;
}

export interface ScientistOfTheDay {
  system: {
    id: string;
  };
  id: string;
  elements: {
    first_name?: StringValue;
    last_name: StringValue;
    image: KontentLinkedMediaImage;
    content_tile: KontentLinkedContentTile;
    page_metadata__page_description: StringValue;
  };
}

export interface ScientistsQuery {
  allKontentItemPage: {
    edges: {
      node: {
        id: string;
        system: {
          id: string;
        };
        elements: {
          content: {
            value: ScientistOfTheDay[];
          };
          url: {
            url: string;
          };
          title: StringValue;
          display_publish_date: StringValue;
        };
      };
    }[];
  };
  allPaginatedCollection: {
    nodes: {
      pages: CollectionPage[];
      pageCount: number;
    }[];
  };
}

export const formatScientist = (scientistData: ScientistOfTheDay) => {
  const contentTiles = formatContentTiles(
    scientistData?.elements?.content_tile?.value
  );

  return {
    id: scientistData?.id,
    systemId: scientistData?.system?.id,
    firstName: scientistData?.elements?.first_name?.value,
    lastName: scientistData?.elements?.last_name?.value,
    image: formatImages(scientistData?.elements?.image?.value[0]?.elements),
    pageDescription:
      scientistData?.elements?.page_metadata__page_description?.value,
    contentTile: contentTiles && contentTiles[0],
  };
};

export const fragmentScientistOfTheDay = graphql`
  fragment kontentScientistOfTheDay on kontent_item_modular_content_element_value {
    value {
      ... on kontent_item_content___sotd {
        id
        system {
          id
        }
        elements {
          first_name {
            value
          }
          last_name {
            value
          }
          image {
            value {
              ...kontentMediaImage
            }
          }
          content_tile {
            value {
              ... on kontent_item_element___content_tile {
                ...kontentContentTile
              }
            }
          }
          page_metadata__page_description {
            value
          }
        }
      }
    }
  }
`;

export const useScientists = (): UseScientists => {
  const scientistsData = useStaticQuery<ScientistsQuery>(graphql`
    query ScientistQuery {
      allKontentItemPage(
        filter: {
          elements: {
            content: {
              value: {
                elemMatch: { system: { type: { eq: "content___sotd" } } }
              }
            }
          }
        }
        sort: { elements: { display_publish_date: { value: DESC } } }
        limit: 13
      ) {
        edges {
          node {
            id
            system {
              id
            }
            elements {
              content {
                ...kontentScientistOfTheDay
              }
              url {
                url
              }
              title {
                value
              }
              display_publish_date {
                value(formatString: "MMMM D, YYYY")
              }
            }
          }
        }
      }
      allPaginatedCollection(filter: { name: { eq: "scientists" } }) {
        nodes {
          pages {
            id
            hasNextPage
            index
          }
          pageCount
        }
      }
    }
  `);

  if (scientistsData.allKontentItemPage.edges.length === 0) {
    return {
      scientists: [],
      pages: [],
    };
  }

  const pages = scientistsData.allPaginatedCollection?.nodes[0]?.pages.sort(
    (left, right) => left.index - right.index
  );

  const scientists = scientistsData.allKontentItemPage?.edges?.map(
    (scientistPage) => {
      const scientistData = formatScientist(
        scientistPage?.node.elements?.content?.value[0]
      );

      const pageInfo = {
        url: scientistPage?.node?.elements?.url.url,
        title: scientistPage?.node?.elements?.title?.value,
        displayPublishDate:
          scientistPage?.node?.elements?.display_publish_date?.value,
      };

      return {
        page: pageInfo,
        ...scientistData,
      };
    }
  );

  return {
    scientists,
    pages,
  };
};
